import { useState } from 'react';
import toast from 'react-hot-toast';
import { Card, CardHeader, Badge, Button, Pagination, getStatusBadgeVariant } from '@/components/common';
import {
  useCommissionLedger,
  usePospRunningBalance,
  useCommissionPayouts,
} from '@/hooks/useCommissions';
import { commissionService } from '@/services/commissions';
import { formatDate, formatCurrency, titleCase } from '@/utils';

/**
 * One agent's money: what they are owed, what has been paid, and every entry
 * behind both — B1.
 *
 * The reported symptom was "there should be a ledger and all" on an agent's
 * page. Nothing was missing from the platform: the agent whose page prompted it
 * had 54 ledger rows with earned, payout and TDS entries and a running balance.
 * They lived on Commissions → Ledger, behind a filter nobody would think to set
 * from an agent's page. This puts them where the question gets asked.
 *
 * OUTSTANDING, NOT EARNINGS. `balance_after` on the newest entry is what the
 * agent is STILL OWED: earnings credit it, payouts and TDS debit it. Calling it
 * earnings would overstate what the broker owes by everything already paid —
 * and it would agree with the "Commission Paid to POSP" figure above it only by
 * coincidence, which is worse than disagreeing.
 *
 * The balance comes from `running_balance?posp_id=`, never `broker_id=`. That
 * endpoint answers both and returns a different number for each; the broker's
 * balance on an agent's page would be plausible and wrong, which is the kind of
 * error nobody catches by looking.
 */

const PAGE_SIZE = 20;

export function PospLedgerPanel({ pospId, pospName }: { pospId: string; pospName?: string }) {
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const ledger = useCommissionLedger(page, { posp: pospId });
  const balance = usePospRunningBalance(pospId);
  // `/commissions/payouts/?posp=` -- the payouts the ledger's payout rows refer
  // to. This first pointed at `/brokers/posps/<id>/payouts/`, which DOES NOT
  // EXIST. The request 404d, the component read that as "no data", and the panel
  // stated that nothing had ever been paid to an agent who had in fact been paid
  // twice. A false statement about somebody's money is worse than an absent one,
  // which is why the failed-vs-empty distinction below is not cosmetic.
  const payouts = useCommissionPayouts(1, { posp: pospId });

  const entries = ledger.data?.results ?? [];
  const total = ledger.data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await commissionService.downloadLedger({ posp: pospId });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ledger-${pospName ? pospName.replace(/\s+/g, '-').toLowerCase() : pospId}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // The export is a convenience over data already on screen; a failure must
      // say so rather than leaving a button that silently does nothing.
      toast.error('Could not export the ledger just now.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card padding="none">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Commission Ledger
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Every entry on this agent's account — earned, paid and TDS
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-gray-500 dark:text-gray-400">Outstanding</p>
            {/* `?? '0'` on a FAILED request printed "₹0.00" in this panel's
                largest type, next to a ledger showing money owed. An unknown
                balance must read as unknown -- react-query leaves isLoading
                false once a request has errored, so the error case needs saying
                out loud rather than falling through to the default. */}
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {balance.isLoading ? '—' : balance.isError ? 'Unavailable' : formatCurrency(balance.data?.balance ?? '0')}
            </p>
            {balance.isError && (
              <p className="text-xs text-amber-700 dark:text-amber-400">Could not load the balance</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} isLoading={exporting}>
            Export CSV
          </Button>
        </div>
      </div>

      {ledger.isLoading ? (
        <p className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400">Loading ledger…</p>
      ) : entries.length === 0 ? (
        <p className="px-6 py-8 text-sm text-gray-500 dark:text-gray-400">
          No ledger entries for this agent yet. Entries appear as commission is earned and paid.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  {['Date', 'Entry', 'Description', 'Amount', 'Balance'].map((header) => (
                    <th
                      key={header}
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatDate(entry.transaction_date)}
                    </td>
                    <td className="px-6 py-3">
                      <Badge
                        variant={entry.transaction_type === 'credit' ? 'success' : 'default'}
                        size="sm"
                      >
                        {titleCase(entry.entry_type)}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-gray-700 dark:text-gray-300">
                      {entry.description}
                    </td>
                    {/* The sign is the point: a credit and a debit of the same
                        amount are not the same row, and a bare number makes
                        them look identical. */}
                    <td
                      className={`px-6 py-3 whitespace-nowrap ${
                        entry.transaction_type === 'credit' ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {entry.transaction_type === 'credit' ? '+' : '−'}
                      {formatCurrency(entry.amount)}
                    </td>
                    <td className="px-6 py-3 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {formatCurrency(entry.balance_after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={PAGE_SIZE}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <div className="border-t border-gray-100 dark:border-gray-800">
        <CardHeader
          title="Payouts"
          subtitle="What has actually been paid to this agent"
        />
        <PayoutList
          rows={payouts.data?.results ?? []}
          isLoading={payouts.isLoading}
          isError={payouts.isError}
        />
      </div>
    </Card>
  );
}

function PayoutList({
  rows,
  isLoading,
  isError,
}: {
  rows: Array<Record<string, any>>;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400">Loading payouts…</p>;
  }
  if (isError) {
    // NEVER "nothing has been paid" on a failed request. Not knowing, and
    // knowing there is nothing, are different facts — and only one of them is
    // safe to tell somebody about their own money.
    return (
      <p className="px-6 pb-6 text-sm text-amber-700 dark:text-amber-400">
        Could not load this agent&apos;s payouts. That is not the same as there being none —
        please try again.
      </p>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-6 pb-6 text-sm text-gray-500 dark:text-gray-400">
        Nothing has been paid out to this agent yet.
      </p>
    );
  }

  return (
    <ul className="px-6 pb-6 space-y-2">
      {rows.map((payout) => (
        <li
          key={payout.id}
          className="flex items-center justify-between gap-3 flex-wrap text-sm border border-gray-100 dark:border-gray-800 rounded px-3 py-2"
        >
          <div>
            {/* `payout_reference` and `payment_date` are the REAL column names.
                The first draft used payout_number/payout_date, which exist on
                neither the model nor the serializer, so every row would have
                rendered blank even once the endpoint was right. */}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {payout.payout_reference}
            </span>
            {payout.payment_date && (
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                {formatDate(payout.payment_date)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {payout.tds_amount != null && Number(payout.tds_amount) > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                TDS {formatCurrency(payout.tds_amount)}
              </span>
            )}
            <span className="text-gray-900 dark:text-gray-100">
              {formatCurrency(payout.net_amount)}
            </span>
            {payout.status && (
              <Badge variant={getStatusBadgeVariant(payout.status)} size="sm">
                {titleCase(payout.status)}
              </Badge>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
